import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search as SearchIcon } from 'lucide-react';
import './MobileSearchBar.css';

const MobileSearchBar = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();

  // Define all searchable pages with their content
  const searchablePages = [
    {
      title: 'Home',
      path: '/',
      keywords: ['home', 'clinical minds', 'psychology', 'mental health', 'coffs harbour', 'sydney', 'telehealth', 'psychologist', 'services']
    },
    {
      title: 'About Us',
      path: '/about',
      keywords: ['about', 'our story', 'team', 'psychologists', 'experience', 'qualifications', 'evidence-based', 'client-centered']
    },
    {
      title: 'Contact',
      path: '/contact',
      keywords: ['contact', 'get in touch', 'phone', 'email', 'address', 'reach us', 'message']
    },
    {
      title: 'Request an Appointment',
      path: '/bookings',
      keywords: ['book', 'appointment', 'booking', 'schedule', 'reserve', 'consultation']
    },
    {
      title: 'Initial Appointments - Bookings',
      path: '/bookings#initial-appointments',
      keywords: ['initial appointment', 'first session', 'new client', 'intake']
    },
    {
      title: 'Follow-up Appointments - Bookings',
      path: '/bookings#follow-up',
      keywords: ['follow-up', 'ongoing', 'continuing therapy', 'regular sessions']
    },
    {
      title: 'Referral Information - Bookings',
      path: '/bookings#referral',
      keywords: ['referral', 'gp referral', 'mental health treatment plan', 'mhtp', 'doctor']
    },
    {
      title: 'Fees & Payment - Bookings',
      path: '/bookings#fees',
      keywords: ['fees', 'cost', 'price', 'payment', 'how much']
    },
    {
      title: 'FAQs',
      path: '/faqs',
      keywords: ['faq', 'questions', 'answers', 'help', 'information']
    },
    {
      title: 'Medicare & Rebates - FAQ',
      path: '/faqs#fees--referrals-mental-health-care-plan',
      keywords: ['medicare', 'rebate', 'bulk billing', 'government', 'subsidy', 'cheaper', 'mental health care plan', 'mhcp']
    },
    {
      title: 'NDIS - FAQ',
      path: '/faqs#fees--referrals-mental-health-care-plan',
      keywords: ['ndis', 'national disability insurance scheme', 'disability support']
    },
    {
      title: 'WorkCover - FAQ',
      path: '/faqs#fees--referrals-mental-health-care-plan',
      keywords: ['workcover', 'work injury', 'workers compensation', 'workplace']
    },
    {
      title: 'Session Duration - FAQ',
      path: '/faqs#sessions--treatment',
      keywords: ['session length', 'how long', 'duration', 'appointment time', '50 minutes']
    },
    {
      title: 'Visiting Clinics',
      path: '/clinics',
      keywords: ['clinic', 'visiting', 'port macquarie', 'sydney', 'melbourne', 'gold coast', 'location']
    },
    {
      title: 'Coffs Harbour Location',
      path: '/locations/coffs-harbour',
      keywords: ['coffs harbour', 'location', 'address', 'clinic', 'office', 'in-person']
    },
    {
      title: 'Telehealth Services',
      path: '/locations/telehealth',
      keywords: ['telehealth', 'online', 'video', 'remote', 'virtual', 'zoom', 'from home']
    },
    {
      title: 'Sydney Location',
      path: '/locations/sydney',
      keywords: ['sydney', 'location', 'address', 'clinic', 'office', 'in-person']
    },
    {
      title: 'Visiting Clinics',
      path: '/clinics',
      keywords: ['visiting', 'clinics', 'port macquarie', 'sydney', 'melbourne', 'gold coast']
    },
    {
      title: 'Healthcare Professional Referrals',
      path: '/refer',
      keywords: ['refer', 'referral', 'gp', 'doctor', 'healthcare professional', 'allied health', 'upload referral', 'mental health care plan']
    },
    {
      title: 'Privacy Policy',
      path: '/privacy-policy',
      keywords: ['privacy', 'policy', 'confidentiality', 'data', 'information', 'gdpr']
    },
    {
      title: 'Terms and Conditions',
      path: '/terms-and-conditions',
      keywords: ['terms', 'conditions', 'agreement', 'legal', 'policy']
    }
  ];

  // Search function
  const performSearch = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = searchablePages.filter(page => {
      const titleMatch = page.title.toLowerCase().includes(lowerQuery);
      const keywordMatch = page.keywords.some(keyword => 
        keyword.toLowerCase().includes(lowerQuery)
      );
      return titleMatch || keywordMatch;
    });

    setSearchResults(results);
  };

  useEffect(() => {
    performSearch(searchQuery);
  }, [searchQuery]);

  const handleResultClick = (path) => {
    // Check if path includes a hash for deep linking
    const [basePath, hash] = path.split('#');
    
    navigate(basePath);
    
    // If there's a hash, scroll to that section after navigation
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300); // Give the page time to load
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-search-overlay">
      <div className="mobile-search-container">
        <div className="mobile-search-header">
          <div className="mobile-search-input-wrapper">
            <SearchIcon size={20} className="search-input-icon" />
            <input
              type="text"
              placeholder="Search site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="mobile-search-input"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="search-clear-btn"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <button onClick={onClose} className="search-close-btn">
            Cancel
          </button>
        </div>

        <div className="mobile-search-results">
          {searchQuery && searchResults.length === 0 && (
            <div className="search-no-results">
              <p>No results found for "{searchQuery}"</p>
            </div>
          )}
          
          {searchResults.length > 0 && (
            <>
              <div className="search-results-header">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </div>
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result.path)}
                  className="search-result-item"
                >
                  <SearchIcon size={16} className="result-icon" />
                  <span className="result-title">{result.title}</span>
                </button>
              ))}
            </>
          )}
          
          {!searchQuery && (
            <div className="search-suggestions">
              <div className="search-suggestions-title">Popular Searches</div>
              <button onClick={() => setSearchQuery('telehealth')} className="search-suggestion-item">Telehealth</button>
              <button onClick={() => setSearchQuery('medicare')} className="search-suggestion-item">Medicare</button>
              <button onClick={() => setSearchQuery('book')} className="search-suggestion-item">Book Appointment</button>
              <button onClick={() => setSearchQuery('fees')} className="search-suggestion-item">Fees & Bulk Billing</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileSearchBar;
